import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useSession } from './useSession';

/**
 * Hook returning every tenant the signed-in user OWNS (user_roles
 * role='tenant_owner'), plus a selected-house cursor for the admin settings
 * pages. Most owners run exactly one house, so `selectedId` resolves to that
 * house automatically and the pages never show a picker. Owners of multiple
 * houses get the full list and a <HouseSelector> to switch — the fix for
 * review finding #9, where "first owner row wins" silently pointed
 * money-adjacent settings at an arbitrary house.
 *
 * Two-step read (roles, then tenants .in(ids)) rather than an embedded join:
 * both tables are RLS-readable for the caller's own rows (AdminBilling has
 * always read them this way), and the split avoids depending on a PostgREST
 * FK-embed shape.
 */
export interface OwnedTenant {
  id: string;
  name: string;
  display_name: string | null;
  /** URL-safe tenant identifier — powers the onboarding preview link
   * (`/preview/<slug>`, spec §4 / Phase E1). */
  slug: string;
  /** Onboarding lifecycle (spec §4): 'draft' = still configuring in sandbox,
   * 'live' = published. Drives whether the Set-up checklist surfaces in nav. */
  publish_state: string;
  /** Onboarding preference: require bidders to have a card on file (a hold) — new
   * auctions default to requires_cc_preauth from this + the amount below. */
  require_bidder_card_on_file: boolean;
  /** Default card-hold amount (cents) new auctions inherit when the above is on. */
  default_cc_preauth_amount_cents: number | null;
  /** Pricing tier — drives the plan panel and every `hasFeature` gate. */
  tier: string;
}

export interface OwnedTenantsState {
  /** Tenants the user owns, name-sorted. Empty while loading or for non-owners. */
  tenants: OwnedTenant[];
  /** The currently selected house — auto-set to the first owned tenant. */
  selectedId: string | null;
  setSelectedId: (id: string) => void;
  loading: boolean;
  error: string | null;
  /** Convenience: the full row for selectedId. */
  selected: OwnedTenant | null;
}

export function useOwnedTenants(): OwnedTenantsState {
  const { userId } = useSession();
  const [tenants, setTenants] = useState<OwnedTenant[]>([]);
  const [selectedId, setSelectedIdState] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (userId === null) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      const { data: roleRows, error: roleErr } = await supabase
        .from('user_roles')
        .select('tenant_id')
        .eq('user_id', userId)
        .eq('role', 'tenant_owner');
      if (cancelled) return;
      if (roleErr) {
        setError(roleErr.message);
        setLoading(false);
        return;
      }

      const ids = [
        ...new Set(
          (roleRows ?? []).map((r) => r.tenant_id).filter((id): id is string => id !== null),
        ),
      ];
      if (ids.length === 0) {
        setTenants([]);
        setSelectedIdState(null);
        setLoading(false);
        return;
      }

      // The card-policy columns aren't in the generated database types yet
      // (regenerates centrally at ship), so cast the result — same idiom as the
      // RPC bridges across the onboarding surface.
      const tenantRes = await supabase
        .from('tenants')
        .select(
          'id, name, display_name, slug, publish_state, require_bidder_card_on_file, default_cc_preauth_amount_cents, tier',
        )
        .in('id', ids)
        .order('name');
      if (cancelled) return;
      if (tenantRes.error) {
        setError(tenantRes.error.message);
        setLoading(false);
        return;
      }

      const rows = (tenantRes.data ?? []) as unknown as OwnedTenant[];
      setTenants(rows);
      // Keep a still-valid prior selection (e.g. across auth refreshes);
      // otherwise default to the first house.
      setSelectedIdState((prev) =>
        prev && rows.some((t) => t.id === prev) ? prev : (rows[0]?.id ?? null),
      );
      setLoading(false);
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const setSelectedId = useCallback(
    (id: string) => {
      if (tenants.some((t) => t.id === id)) setSelectedIdState(id);
    },
    [tenants],
  );

  const selected = tenants.find((t) => t.id === selectedId) ?? null;

  return { tenants, selectedId, setSelectedId, loading, error, selected };
}
